import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect
} from 'react';
import { Box, CircularProgress, makeStyles } from '@material-ui/core';
import { createForm } from 'final-form';
import { Form, FormSpy, Field } from 'react-final-form';
import { KeyboardDatePicker, Select, TextField } from 'mui-rff';
import {
  fieldLengthValidator,
  formatDate,
  isValidDate
} from '../../../core/utils';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { useGetList, RadioButtonGroupInput } from 'react-admin';
import {
  emailFormatter,
  emailParser,
  STEP,
  validateEmailAllowEmpty,
  validateFundIdRff,
  validateTargetCommitment,
  validateTargetNbOfInvestors
} from './utils';
import { LocalizedUtils } from '../../../core/components/forked/input/DateInput';
import { ErrorIcon } from '../../../core/themes/icons/ErrorIcon';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import FundApi from '../../../core/apis/FundApi';
import { isEmpty } from 'lodash';
import { CreationFundContext } from './CreationFundContext';
import omit from 'lodash/omit';
import { fundTypes, fundStatus } from '../FundLookups';
import SelectMainFundModal from '../../../features/funds/SelectMainModal';
import CheckBoxField from '../../../core/components/CheckBoxField';
import TenantConfigurationApi from '../../../core/apis/TenantConfigurationApi';
import {
  INVESTMENT_STRUCTURE_OPTIONS,
  mapInvestmentStructureToBooleans,
  deriveInvestmentStructureFromBooleans
} from '../../../features/funds/InvestmentStructureUtil';
import RequiredLabel from '../RequiredLabel';
import MainFundSection from '../MainFundSection';
import { sharedFundStyles } from '../fundStyles';

const fundNameMaxLength = 256;
const fundIdMaxLength = 20;

const useStyle = makeStyles({
  ...sharedFundStyles,
  center: {
    display: 'grid',
    placeItems: 'center',
    height: '66%'
  },
  addLabel: {
    color: '#2962FF'
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(200px, 500px))',
    columnGap: '24px',
    rowGap: '8px',
    alignItems: 'start',
    '& .MuiFormControl-marginDense': {
      marginTop: 0,
      marginBottom: 0
    },
    '& .MuiFormHelperText-root:not(.Mui-error)': {
      display: 'none'
    },
    '& .MuiFormLabel-asterisk, & .MuiInputLabel-asterisk': {
      color: 'red !important'
    }
  }
});

const FundDetailsStep = ({
  setIsNextAvailable,
  setIsSpinnerOnNextButton,
  onSubmitEnd,
  setIsFirstStepPristine,
  fundCreatedRef,
  setListOfSteps,
  listOfSteps
}) => {
  const classes = useStyle();
  const notifyServerError = useNotifyServerError();
  const { fundContext, setFundContext } = useContext(CreationFundContext);
  const formStateRef = useRef({});

  const [mainFundModalOpen, setMainFundModalOpen] = useState(false);
  const [selectedMainFund, setSelectedMainFund] = useState(
    fundContext.fund?.mainFund || null
  );
  const [fundSelfServeEnabled, setFundSelfServeEnabled] = useState(false);

  const handleSelectMainFund = fund => {
    setSelectedMainFund(fund);
    formRef.current.change('mainFundId', fund.id);
    formRef.current.change('mainFundName', fund.name);
  };

  const onSubmit = useCallback(
    async values => {
      const handleCreateFund = async (vals, coInvestment, sma) => {
        const payload = {
          ...omit(vals, ['investmentStructure']),
          coInvestment,
          sma,
          inceptionDate: formatDate(vals.inceptionDate)
        };
        const response = await FundApi.create(payload, vals.id, {
          fundBillingAcknowledgement: vals.acknowledge
        });
        setFundContext({ ...fundContext, fund: response });
        fundCreatedRef.current = response;
        const updatedListOfStep = listOfSteps.map(step =>
          step.name === STEP.fundDetails ? { ...step, completed: true } : step
        );
        setListOfSteps(updatedListOfStep);
      };

      const handleEditFund = async vals => {
        const payload = {
          ...omit(vals, 'id'),
          inceptionDate: formatDate(vals.inceptionDate)
        };
        const response = await FundApi.edit(payload, fundContext.fund.id);
        setFundContext({ ...fundContext, fund: response });
      };

      const pristine = formStateRef.current.pristine;
      setIsSpinnerOnNextButton(true);

      const { coInvestment, sma } = mapInvestmentStructureToBooleans(
        values.investmentStructure
      );

      try {
        if (!pristine && fundContext.fund === undefined) {
          await handleCreateFund(values, coInvestment, sma);
        } else if (!pristine && fundContext.fund !== undefined) {
          await handleEditFund(values);
        }
        setIsNextAvailable(true);
        onSubmitEnd();
      } catch (e) {
        notifyServerError(e, undefined);
      } finally {
        setIsSpinnerOnNextButton(false);
      }
    },
    [
      setIsSpinnerOnNextButton,
      fundContext,
      setFundContext,
      fundCreatedRef,
      setIsNextAvailable,
      listOfSteps,
      setListOfSteps,
      onSubmitEnd,
      notifyServerError
    ]
  );

  const validateAcknowledgeIfSelfServe = value => {
    if (fundSelfServeEnabled && value !== true) {
      return 'You must acknowledge before proceeding.';
    }
    return undefined;
  };

  const {
    data: dataFundClasses,
    loading: fundClassesLoading,
    error: fundClassesError
  } = useGetList('fundclasses', null, { field: 'description', order: 'ASC' });

  const fundClasses = Object.values(dataFundClasses).map(fundClass => ({
    value: fundClass.classCode,
    label: fundClass.description
  }));

  const {
    data: dataCurrencies,
    loading: currenciesLoading,
    error: currenciesError
  } = useGetList('currencies', null, 'ASC');

  const currencies = Object.values(dataCurrencies).map(currency => ({
    value: currency.id,
    label: currency.code
  }));

  const {
    data: dataFundFamilies,
    loading: fundFamiliesLoading,
    error: fundFamiliesError,
    total: fundFamilyCount
  } = useGetList('fundfamilies', null, undefined, {
    orphanVisibility: 'ALL_VISIBLE',
    orderBy: 'id',
    orderDirection: 'DESC'
  });

  const fundFamilies = Object.values(dataFundFamilies).map(fundFamily => ({
    value: fundFamily.id,
    label: fundFamily.name
  }));

  const validateFundDetails = values => {
    const errors = {};
    // no cross-field error needed anymore
    return errors;
  };

  const initialValues = isEmpty(fundContext.fund)
    ? null
    : {
        ...fundContext.fund,
        investmentStructure: deriveInvestmentStructureFromBooleans(
          fundContext.fund?.coInvestment,
          fundContext.fund?.sma
        )
      };

  const formRef = useRef(
    createForm({
      onSubmit: onSubmit,
      initialValues,
      validate: validateFundDetails
    })
  );

  const isEmptyValue = v =>
    v === undefined ||
    v === null ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0);

  const required = value => (isEmptyValue(value) ? 'Required' : undefined);

  const composeValidators = (...validators) => (value, values, fieldState) =>
    validators.reduce(
      (error, validator) => error || validator(value, values, fieldState),
      undefined
    );

  const requiredIfSelfServe = field =>
    fundSelfServeEnabled ? [required()] : [];

  useEffect(() => {
    TenantConfigurationApi.get('Fund Self-Serve')
      .then(response => {
        setFundSelfServeEnabled(response?.pageData[0]?.value === '1');
      })
      .catch(error => notifyServerError(error));
  }, [notifyServerError]);

  function handleFormChange({ valid, submitting, pristine, values, errors }) {
    const shouldDisableNext = isNextDisabled({
      valid,
      submitting,
      values,
      errors
    });

    setIsNextAvailable(!shouldDisableNext);
    setIsFirstStepPristine(pristine);

    formStateRef.current = { pristine, valid, errors, values };
    setFundContext(prev => ({
      ...prev,
      detailsFormState: { pristine, valid, errors, values }
    }));
  }

  function isNextDisabled({ valid, submitting, values, errors }) {
    if (!valid || submitting) return true;

    const hasDataRoomError = Boolean(errors?.dataRoom);
    if (hasDataRoomError) return true;

    return values.investmentStructure === 'Co-Investment' && !values.mainFundId;
  }

  if (fundClassesLoading || currenciesLoading || fundFamiliesLoading) {
    return (
      <Box className={classes.center}>
        <CircularProgress />
      </Box>
    );
  }

  if (fundClassesError || currenciesError || fundFamiliesError) {
    return (
      <Box className={classes.center}>
        <ErrorIcon />
      </Box>
    );
  }

  return (
    <Form form={formRef.current}>
      {({ handleSubmit, submitting }) => (
        <>
          <FormSpy
            subscription={{
              valid: true,
              pristine: true,
              submitting: true,
              values: true,
              errors: true
            }}
            onChange={props => handleFormChange(props)}
          />
          <form onSubmit={handleSubmit} id="formFundDetails" noValidate>
            <FundDetailsFormContent
              classes={classes}
              submitting={submitting}
              fundSelfServeEnabled={fundSelfServeEnabled}
              fundContext={fundContext}
              formRef={formRef}
              required={required}
              composeValidators={composeValidators}
              requiredIfSelfServe={requiredIfSelfServe}
              validateAcknowledgeIfSelfServe={validateAcknowledgeIfSelfServe}
              currencies={currencies}
              fundTypes={fundTypes}
              fundFamilies={fundFamilies}
              fundFamilyCount={fundFamilyCount}
              fundClasses={fundClasses}
              selectedMainFund={selectedMainFund}
              setSelectedMainFund={setSelectedMainFund}
              mainFundModalOpen={mainFundModalOpen}
              setMainFundModalOpen={setMainFundModalOpen}
              handleSelectMainFund={handleSelectMainFund}
            />
          </form>
        </>
      )}
    </Form>
  );
};

const selfServeLabel = (enabled, label) =>
  enabled ? <RequiredLabel label={label} /> : label;

const FundDetailsFormContent = ({
  classes,
  submitting,
  fundSelfServeEnabled,
  fundContext,
  formRef,
  required,
  composeValidators,
  requiredIfSelfServe,
  validateAcknowledgeIfSelfServe,
  currencies,
  fundTypes,
  fundFamilies,
  fundFamilyCount,
  fundClasses,
  selectedMainFund,
  setSelectedMainFund,
  mainFundModalOpen,
  setMainFundModalOpen,
  handleSelectMainFund
}) => (
  <>
    <div className={classes.gridContainer}>
      {/* Row 1 */}
      <div className={classes.formGroup}>
        <TextField
          name="name"
          label={<RequiredLabel label="Fund Name" />}
          fieldProps={{
            validate: composeValidators(
              fieldLengthValidator(fundNameMaxLength),
              required
            )
          }}
          disabled={submitting}
          id="fund-name"
        />
      </div>
      <div className={classes.formGroup}>
        <MuiPickersUtilsProvider utils={LocalizedUtils}>
          <KeyboardDatePicker
            label={<RequiredLabel label="Fund Inception Date" />}
            name="inceptionDate"
            placeholder="MM/dd/yyyy"
            format="MM/dd/yyyy"
            fieldProps={{
              validate: composeValidators(required, isValidDate)
            }}
            parse={formatDate}
            disabled={submitting}
          />
        </MuiPickersUtilsProvider>
      </div>
      <div className={classes.dataRoomGroup}>
        <Field
          name="dataRoom"
          validate={fundSelfServeEnabled ? required : undefined}
          subscription={{}}
        >
          {() => null}
        </Field>
        <RadioButtonGroupInput
          source="dataRoom"
          label={selfServeLabel(fundSelfServeEnabled, 'Data Room')}
          margin="none"
          classes={{ label: classes.label }}
          options={{ row: true }}
          choices={[
            { id: true, name: 'Yes' },
            { id: false, name: 'No' }
          ]}
          validate={fundSelfServeEnabled ? [required] : []}
          optionText="name"
          optionValue="id"
          onChange={value => {
            // keep final-form in sync so it can validate/disable correctly
            formRef.current.change('dataRoom', value);
          }}
        />
      </div>

      {/* Row 2 */}
      <div className={classes.formGroup}>
        <TextField
          name="id"
          label={<RequiredLabel label="Fund ID" />}
          fieldProps={{
            validate: composeValidators(
              fieldLengthValidator(fundIdMaxLength),
              required,
              validateFundIdRff
            )
          }}
          disabled={submitting || !isEmpty(fundContext.fund)}
          id="fund-id"
        />
      </div>
      <div className={classes.formGroup}>
        <Select
          name="currency"
          label={<RequiredLabel label="Currency" />}
          data={currencies}
          fieldProps={{ validate: required }}
          disabled={submitting}
        />
      </div>
      <div className={classes.formGroup}>
        <Select
          name="investmentStructure"
          label={<RequiredLabel label="Investment Structure" />}
          data={INVESTMENT_STRUCTURE_OPTIONS}
          fieldProps={{ validate: required }}
          disabled={submitting}
        />
      </div>

      {/* Row 3 */}
      <div className={classes.formGroup}>
        <Select
          name="fundType"
          label={selfServeLabel(fundSelfServeEnabled, 'Fund Type')}
          data={fundTypes}
          fieldProps={{
            validate: fundSelfServeEnabled ? required : undefined
          }}
          disabled={submitting}
        />
      </div>
      <div className={classes.formGroup}>
        <TextField
          name="targetCommitment"
          label={selfServeLabel(fundSelfServeEnabled, 'Target Commitment')}
          fieldProps={{
            validate: fundSelfServeEnabled
              ? composeValidators(required, validateTargetCommitment)
              : validateTargetCommitment
          }}
          disabled={submitting}
        />
      </div>
      <MainFundSectionWrapper
        selectedMainFund={selectedMainFund}
        setSelectedMainFund={setSelectedMainFund}
        setMainFundModalOpen={setMainFundModalOpen}
        formRef={formRef}
        classes={classes}
      />

      {/* Row 4 */}
      {fundFamilyCount > 0 ? (
        <div className={classes.formGroup}>
          <Select
            name="fundFamilyId"
            label="Fund Family"
            data={fundFamilies}
            disabled={submitting}
            id="fund-family-id"
          />
        </div>
      ) : (
        <div />
      )}
      <div className={classes.formGroup}>
        <TextField
          name="targetNbOfInvestors"
          label={selfServeLabel(
            fundSelfServeEnabled,
            'Target Number of Investors'
          )}
          fieldProps={{
            validate: fundSelfServeEnabled
              ? composeValidators(required, validateTargetNbOfInvestors)
              : validateTargetNbOfInvestors
          }}
          disabled={submitting}
        />
      </div>
      <div className={classes.formGroup}>
        <Select
          name="status"
          label={<RequiredLabel label="Publishing Status" />}
          data={fundStatus}
          fieldProps={{ validate: required }}
          disabled={submitting}
        />
      </div>

      {/* Row 5 */}
      <div className={classes.formGroup}>
        <TextField
          name="emailAddresses"
          label="Fund Contact Email"
          fieldProps={{
            validate: composeValidators(
              validateEmailAllowEmpty,
              fieldLengthValidator(500)
            ),
            format: emailFormatter,
            parse: emailParser
          }}
          disabled={submitting}
        />
      </div>
      <div className={classes.formGroup}>
        <Select
          name="classCode"
          label={<RequiredLabel label="Asset Class" />}
          data={fundClasses}
          fieldProps={{ validate: required }}
          disabled={submitting}
        />
      </div>
      {fundSelfServeEnabled ? (
        <div className={classes.formGroup}>
          <Field
            name="acknowledge"
            component={CheckBoxField}
            label={
              <span>
                I acknowledge that the creation of this fund may be subject to
                an additional fee that will be communicated via an order form.
                <span style={{ color: 'red' }}>{' *'}</span>
              </span>
            }
            type="checkbox"
            parse={value => {
              return !!value;
            }}
            defaultValue={false}
            validate={validateAcknowledgeIfSelfServe}
          />
        </div>
      ) : (
        <div />
      )}
    </div>

    <SelectMainFundModal
      open={mainFundModalOpen}
      onClose={() => setMainFundModalOpen(false)}
      onSelect={handleSelectMainFund}
    />
  </>
);

const MainFundSectionWrapper = ({
  selectedMainFund,
  setSelectedMainFund,
  setMainFundModalOpen,
  formRef,
  classes
}) => (
  <div className={classes.mainFundGroup}>
    <FormSpy subscription={{ values: true }}>
      {({ values }) => {
        const isCoInvestment = values.investmentStructure === 'Co-Investment';
        const isMainFundSelected = Boolean(values.mainFundId);
        const showMainFundError = isCoInvestment && !isMainFundSelected;

        return (
          <div style={{ width: '100%' }}>
            <MainFundSection
              isCoInvestment={isCoInvestment}
              showMainFundError={showMainFundError}
              hasSelectedFund={Boolean(values.mainFundId)}
              classes={classes}
              onClear={() => {
                setSelectedMainFund(null);
                formRef.current.change('mainFundId', null);
                formRef.current.change('mainFundName', null);
              }}
              onSelect={() => setMainFundModalOpen(true)}
              renderSelectedFund={isCo => (
                <TextField
                  name="mainFundName"
                  label={`Main Fund${isCo ? ' *' : ''}`}
                  disabled
                  variant="standard"
                  fullWidth
                />
              )}
            />
          </div>
        );
      }}
    </FormSpy>
  </div>
);

export default FundDetailsStep;
